import { useEffect, useRef } from 'react';
import { useFilters } from '../../FilterContext';
import { getDependencyChange } from './filterDependencies';

/**
 * Custom hook for handling filter initialization with URL and localStorage persistence
 * 
 * @param {string} filterKey - The filter key (e.g., 'model', 'department', 'timeframe')
 * @param {Array} dataSource - The array of options/items for this filter
 * @param {Object} config - Configuration object
 * @param {Function} config.getDefaultValue - Function that returns the default value when no URL or localStorage value exists
 * @param {Function} config.getParams - Function that extracts API parameters from the selected item
 * @param {Function} config.findItem - Optional custom function to find item by ID (defaults to generic ID matching)
 * @param {Array} config.dependencies - Array of values that should trigger re-initialization when changed
 * @param {boolean} config.resetOnDependencyChange - Whether to reset when dependencies change
 * @param {boolean} config.waitForData - Whether to wait for dataSource to have items before initializing
 * @param {boolean} config.persistAll - Whether to persist 'all' values to localStorage/URL when Show All is selected
 */
export const useFilterInitialization = (filterKey, dataSource, config = {}) => {
  const {
    getDefaultValue = () => ({}),
    getParams = (item) => ({ [`${filterKey}Id`]: item.id }),
    // Optional: maps the selected value to the backend report-DTO shape
    // (plural id arrays for the *SearchPostRequest DTOs). When omitted, the
    // filter's .params is used at report-fire time, which is correct for
    // filters whose params already match the DTO field names (timeframe,
    // statusType, sort, etc).
    getReportParams = null,
    findItem = null, // Will use generic findItemById if not provided
    dependencies = [],
    resetOnDependencyChange = false,
    waitForData = true,
    persistAll = false,
    multiSelect = false
  } = config;

  const { filterValues, updateFilter, getInitialFilterValue, initialValues, storageKey, siteAppId } = useFilters();
  const hasInitializedRef = useRef(false);
  const lastDependenciesRef = useRef(dependencies);
  const hasInitialValueRef = useRef(!!initialValues[filterKey]);

  // Generic item finder that handles string/number ID matching
  const findItemById = (items, id) => {
    return items.find(item => 
      item.id === id || 
      item.id === parseInt(id) || 
      String(item.id) === String(id)
    );
  };

  const findItemInDataSource = findItem || findItemById;

  const { hasChanged, isInitialSettle } = getDependencyChange(lastDependenciesRef.current, dependencies);

  // A dependency settling from unset on first load is initialisation, not the
  // user picking a different parent filter. Resetting there would throw away the
  // value the URL or localStorage asked us to restore. It applies once: after
  // that the restored value is no longer what the filter holds.
  const preservesInitialValue = hasInitialValueRef.current && isInitialSettle;

  // Reset whenever the filter holds a value and its dependencies change. The
  // filter can hold a value this hook never set, because selecting from the
  // dropdown calls updateFilter directly.
  const hasValue = !!filterValues[filterKey];

  useEffect(() => {
    if (!resetOnDependencyChange || !hasChanged || !hasValue) {
      return;
    }

    if (preservesInitialValue) {
      hasInitialValueRef.current = false;
      lastDependenciesRef.current = [...dependencies];
      return;
    }

    const defaultValue = getDefaultValue();
    const params = getParams(defaultValue);
    const reportParams = getReportParams ? getReportParams(defaultValue) : undefined;

    updateFilter(filterKey, defaultValue, params, persistAll, reportParams);
    hasInitializedRef.current = false;
    lastDependenciesRef.current = [...dependencies];
  }, [dependencies, resetOnDependencyChange, hasChanged, hasValue, preservesInitialValue, getDefaultValue, getParams, getReportParams, updateFilter, filterKey, persistAll]);

  // Main initialization logic
  useEffect(() => {
    const shouldInitialize = !hasInitializedRef.current && 
                           (!waitForData || dataSource.length > 0) && 
                           !filterValues[filterKey];

    if (shouldInitialize) {
      let selectedValue;
      
      // Special handling for chips filter - chips don't need dataSource searching
      if (filterKey === 'chips') {
        // For chips, use initialValues directly (URL/localStorage chips) or default
        selectedValue = initialValues[filterKey] || getDefaultValue();
      } else {
        // For other filters, use the normal getInitialFilterValue logic
        const defaultValue = getDefaultValue();

        if (multiSelect) {
          // For multi-select filters (use arrays internally)
          const storedValue = initialValues[filterKey];
          if (Array.isArray(storedValue)) {
            // URL had multiple parameters - find matching items
            const matchedItems = storedValue
              .map(id => findItemInDataSource(dataSource, id))
              .filter(item => item !== undefined);
            selectedValue = matchedItems.length > 0 ? matchedItems : defaultValue;
          } else if (storedValue) {
            // Single URL parameter - find the item
            const foundItem = findItemInDataSource(dataSource, storedValue);
            selectedValue = foundItem ? [foundItem] : defaultValue;
          } else {
            selectedValue = defaultValue;
          }
        } else {
          // For traditional single-select filters (existing behavior)
          const defaultValue = getDefaultValue();
          selectedValue = getInitialFilterValue(filterKey, dataSource, defaultValue, findItemInDataSource);
        }
      }
      
      // Let the filter's getParams function handle the value appropriately
      const params = getParams(selectedValue);
      const reportParams = getReportParams ? getReportParams(selectedValue) : undefined;

      updateFilter(filterKey, selectedValue, params, persistAll, reportParams);
      hasInitializedRef.current = true;
      lastDependenciesRef.current = [...dependencies];
    }
  }, [
    filterKey,
    dataSource,
    filterValues,
    getInitialFilterValue,
    getDefaultValue,
    getParams,
    updateFilter,
    waitForData,
    findItemInDataSource,
    dependencies,
    storageKey,
    siteAppId,
    persistAll
  ]);

  return {
    isInitialized: hasInitializedRef.current,
    reinitialize: () => {
      hasInitializedRef.current = false;
    }
  };
}; 
